import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users, UserRound, Map } from "lucide-react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { TabsHeader } from "@/components/ui/tabs-header";
import { PeriodFilter } from "@/components/ui/period-filter";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { AdvisorMembersTab } from "@/lib/dashboard/AdvisorMembersTab";
import { ClientsTab } from "@/lib/dashboard/clients/ClientsTab";
import { TripsTab } from "@/lib/dashboard/trips/TripsTab";
import { PERIOD_OPTIONS } from "@/lib/dashboard/periodOptions";
import { AgencyDashboardPeriod } from "@/integrations/supabase/rpc-types";

const AgencyOverview = () => {
  const { t } = useI18n();
  const { isSidebarCollapsed } = useSidebar();
  const { userRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<AgencyDashboardPeriod>("30d");

  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  // Salto dalla scheda di un consulente a quella di un suo cliente: cambia tab e porta con
  // se' la chiave, che ClientsTab consuma una volta sola. Passa dallo stato e non dall'URL
  // perche' e' un'apertura, non una posizione a cui tornare col back del browser.
  const [pendingClientKey, setPendingClientKey] = useState<string | null>(null);
  const handleOpenClient = (clientKey: string) => {
    setPendingClientKey(clientKey);
    setSearchParams({ tab: "clients" });
  };

  const showAdvisorsTab = userRole !== "travel_advisor_member" && userRole !== "travel_designer";
  // La tab Clienti c'e' per tutti i ruoli della pagina: cambia solo cosa ci si trova
  // dentro, che e' il perimetro gia' deciso dalla RPC. Il filtro per advisor invece ha
  // senso solo per chi vede il lavoro di piu' persone.
  const canFilterByAdvisor = showAdvisorsTab;

  // La panoramica KPI e' una pagina a se' (/overview): qui restano le liste di dettaglio.
  const tabs = [
    ...(showAdvisorsTab ? [{ value: "advisors", label: t("dashboard.overview.tabs.advisors"), icon: Users }] : []),
    { value: "clients", label: t("dashboard.overview.tabs.clients"), icon: UserRound },
    { value: "trips", label: t("dashboard.overview.tabs.trips"), icon: Map },
  ];
  const requestedTab = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.value === requestedTab) ? requestedTab! : tabs[0].value;

  return (
    <PrivateLayout
      pageTitle={t("dashboard.overview.dataPageTitle")}
      pageDescription={t("dashboard.overview.dataPageDescription")}
      showBackButton={false}
    >
      <TabsHeader
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
        sidebarCollapsed={isSidebarCollapsed}
      />

      <div className="xl:px-8 md:px-4 mt-4 mx-auto space-y-4">
        {/* Period selector */}
        <div className="flex justify-end">
          <PeriodFilter
            options={PERIOD_OPTIONS.map((opt) => ({
              value: opt.value,
              label: t(opt.labelKey),
            }))}
            value={period}
            onChange={(value) => setPeriod(value as AgencyDashboardPeriod)}
            ariaLabel={t("dashboard.overview.periodLabel")}
          />
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {showAdvisorsTab && (
            <ProfileTabsContent value="advisors">
              <AdvisorMembersTab period={period} onOpenClient={handleOpenClient} />
            </ProfileTabsContent>
          )}
          <ProfileTabsContent value="clients">
            <ClientsTab
              period={period}
              canFilterByAdvisor={canFilterByAdvisor}
              openClientKey={pendingClientKey}
              onOpenedClient={() => setPendingClientKey(null)}
            />
          </ProfileTabsContent>
          <ProfileTabsContent value="trips">
            <TripsTab period={period} canFilterByAdvisor={canFilterByAdvisor} />
          </ProfileTabsContent>
        </Tabs>
      </div>
    </PrivateLayout>
  );
};

export default AgencyOverview;
